/**
 * TypeChip.tsx — colored chip for a Voucher.type value.
 *
 * 20 distinct colour schemes (Tailwind `bg-{c}-100 text-{c}-800` pattern)
 * picked to roughly mirror Tally Prime's conventions:
 *   - Sales / income flows = green family
 *   - Purchase / expense flows = orange/amber family
 *   - Payment outflows = teal/sky family
 *   - Receipt inflows = emerald family
 *   - Journal / adjustments = indigo/slate family
 *   - Cash & bank movements = blue family
 *   - Period-end / FX / non-cash = purple/violet/rose family
 *
 * IMPORTANT: every class name is enumerated as a string literal so Tailwind's
 * JIT can find it at build-time (no dynamic `bg-${color}-100` interpolation).
 */

import React from 'react';

export type VoucherTypeKey = string;

interface ChipStyle {
  bg: string;
  text: string;
  border: string;
  label: string;
}

/**
 * Canonical palette. Tailwind class literals only — no template strings.
 * `border` is used in the filter-bar chip state (active = border + bg, inactive = border only).
 */
export const VOUCHER_TYPE_STYLES: Record<string, ChipStyle> = {
  SALES_INVOICE: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    label: 'Sales Invoice',
  },
  SALES: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    label: 'Sales',
  },
  PURCHASE_INVOICE: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300',
    label: 'Purchase Invoice',
  },
  PURCHASE: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300',
    label: 'Purchase',
  },
  PAYMENT: {
    bg: 'bg-teal-100',
    text: 'text-teal-800',
    border: 'border-teal-300',
    label: 'Payment',
  },
  RECEIPT: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    border: 'border-emerald-300',
    label: 'Receipt',
  },
  JOURNAL: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-800',
    border: 'border-indigo-300',
    label: 'Journal',
  },
  CONTRA: {
    bg: 'bg-sky-100',
    text: 'text-sky-800',
    border: 'border-sky-300',
    label: 'Contra',
  },
  BANK_ENTRY: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-300',
    label: 'Bank Entry',
  },
  CASH_ENTRY: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-300',
    label: 'Cash Entry',
  },
  CREDIT_CARD_ENTRY: {
    bg: 'bg-fuchsia-100',
    text: 'text-fuchsia-800',
    border: 'border-fuchsia-300',
    label: 'Credit Card',
  },
  INTER_COMPANY: {
    bg: 'bg-slate-100',
    text: 'text-slate-800',
    border: 'border-slate-300',
    label: 'Inter-Company',
  },
  EXCISE: {
    bg: 'bg-stone-100',
    text: 'text-stone-800',
    border: 'border-stone-300',
    label: 'Excise',
  },
  WRITE_OFF: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
    label: 'Write-Off',
  },
  OPENING: {
    bg: 'bg-lime-100',
    text: 'text-lime-800',
    border: 'border-lime-300',
    label: 'Opening',
  },
  DEPRECIATION: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-300',
    label: 'Depreciation',
  },
  EXCHANGE_RATE_REVALUATION: {
    bg: 'bg-violet-100',
    text: 'text-violet-800',
    border: 'border-violet-300',
    label: 'FX Revaluation',
  },
  EXCHANGE_GAIN_LOSS: {
    bg: 'bg-pink-100',
    text: 'text-pink-800',
    border: 'border-pink-300',
    label: 'FX Gain/Loss',
  },
  DEFERRED_REVENUE: {
    bg: 'bg-cyan-100',
    text: 'text-cyan-800',
    border: 'border-cyan-300',
    label: 'Deferred Revenue',
  },
  DEFERRED_EXPENSE: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-300',
    label: 'Deferred Expense',
  },
  ITC_REVERSAL: {
    bg: 'bg-rose-100',
    text: 'text-rose-800',
    border: 'border-rose-300',
    label: 'ITC Reversal',
  },
  ITC_RECLAIM: {
    bg: 'bg-teal-100',
    text: 'text-teal-800',
    border: 'border-teal-300',
    label: 'ITC Reclaim',
  },
  // Legacy AURA aliases.
  CREDIT_NOTE: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
    label: 'Credit Note',
  },
  DEBIT_NOTE: {
    bg: 'bg-rose-100',
    text: 'text-rose-800',
    border: 'border-rose-300',
    label: 'Debit Note',
  },
  MEMORANDUM: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-300',
    label: 'Memorandum',
  },
};

const DEFAULT_STYLE: ChipStyle = {
  bg: 'bg-gray-100',
  text: 'text-gray-800',
  border: 'border-gray-300',
  label: '',
};

export function getVoucherTypeStyle(type: string): ChipStyle {
  return VOUCHER_TYPE_STYLES[type] ?? { ...DEFAULT_STYLE, label: type.replace(/_/g, ' ') };
}

export function getVoucherTypeLabel(type: string): string {
  return getVoucherTypeStyle(type).label || type;
}

interface TypeChipProps {
  type: string;
  size?: 'sm' | 'md';
  /** Render as toggleable filter pill. Inactive = white bg + grey ring. */
  interactive?: boolean;
  active?: boolean;
  onClick?: () => void;
  className?: string;
}

const TypeChip: React.FC<TypeChipProps> = ({
  type,
  size = 'sm',
  interactive = false,
  active = true,
  onClick,
  className = '',
}) => {
  const style = getVoucherTypeStyle(type);
  const sizeClass =
    size === 'md' ? 'text-xs px-2.5 py-1' : 'text-[11px] leading-4 px-2 py-0.5';

  if (interactive) {
    const inactiveClass =
      'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700';
    const activeClass = `${style.bg} ${style.text} ${style.border}`;
    return (
      <button
        type="button"
        data-testid={`voucher-type-filter-chip-${type}`}
        onClick={onClick}
        className={`inline-flex items-center rounded-full border font-medium transition-colors ${sizeClass} ${
          active ? activeClass : inactiveClass
        } ${className}`}
        aria-pressed={active}
      >
        {style.label}
      </button>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${sizeClass} ${style.bg} ${style.text} ${style.border} ${className}`}
    >
      {style.label}
    </span>
  );
};

export default TypeChip;
